import { parseAsNativeArrayOf, parseAsString, useQueryState } from "nuqs";
import { useMemo, useCallback } from "react";

import { ArchitectureTypeSize } from "../../../../__generated__/gql/graphql";

/** URL パラメータ名（reservation-calendar 側と共通の語彙・形式） */
const PARAM_NAME = "arch";

/** Large フィルタの値（シリーズとは別の AND 条件として arch に畳み込む） */
const LARGE_VALUE = ArchitectureTypeSize.Large;

// nuqs parser for architecture type filter (?arch=MOSS&arch=Large)
const architectureFilterParser = parseAsNativeArrayOf(parseAsString);

/**
 * 建築タイプフィルタ（Large を含む）の状態管理とURL同期を行うカスタムフック
 *
 * - `arch` の単一パラメータに、シリーズと Large を畳み込んで持つ
 *   （reservation-calendar 側と同じ URL 形式: `?arch=MOSS&arch=Large`）
 * - series と largeOnly は読み側で分解し、書き側は 1 つの配列に合成して
 *   1 回の set で書く（同一キーへの二重 set は競合するため避ける）
 */
export function useArchitectureTypeFilter() {
  const [urlFilter, setUrlFilter] = useQueryState(
    PARAM_NAME,
    architectureFilterParser,
  );

  // URL の文字列配列から Large を除いた Set<string>（シリーズ部分）
  const architectureFilter = useMemo(
    () => new Set(urlFilter.filter((param) => param !== LARGE_VALUE)),
    [urlFilter],
  );

  // URL の文字列配列に Large が含まれるか
  const largeOnly = useMemo(() => urlFilter.includes(LARGE_VALUE), [urlFilter]);

  const writeArchitectureFilter = useCallback(
    (series: Set<string>, large: boolean) => {
      const next = large ? [...series, LARGE_VALUE] : [...series];
      void setUrlFilter(next.length > 0 ? next : null);
    },
    [setUrlFilter],
  );

  const toggleArchitectureType = useCallback(
    (key: string) => {
      const next = new Set(architectureFilter);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeArchitectureFilter(next, largeOnly);
    },
    [architectureFilter, largeOnly, writeArchitectureFilter],
  );

  const toggleLargeOnly = useCallback(() => {
    writeArchitectureFilter(architectureFilter, !largeOnly);
  }, [architectureFilter, largeOnly, writeArchitectureFilter]);

  // ポップアップ「決定」・セクション「クリア」用の一括適用。
  const applyArchitectureConditions = useCallback(
    (archTypes: string[], large: boolean) => {
      writeArchitectureFilter(new Set(archTypes), large);
    },
    [writeArchitectureFilter],
  );

  return {
    /** 現在の建築タイプフィルタ状態（Large を除いたシリーズの Set） */
    architectureFilter,
    /** Large のみで絞り込むかどうか */
    largeOnly,
    /** 建築タイプの選択状態をトグルし、URL に同期する（largeOnly は保持） */
    toggleArchitectureType,
    /** Large 条件をトグルし、URL に同期する（シリーズ部分は保持） */
    toggleLargeOnly,
    /** 建築タイプ・Large 条件を一括で適用する（ポップアップ決定・クリア用） */
    applyArchitectureConditions,
  };
}
